import { useState } from 'react';
import { Col, Grid, Row, theme } from 'antd';

import { space } from '@contexts/themeTokens';
import { useProjectTypeFilter } from '@hooks/useProjectTypeFilter';

import ProjectTypeFilter from '@components/filters/ProjectTypeFilter';
import View from '@components/layout/views/View';

import ObjectDetails from './components/ObjectDetails';
import ObjectList from './components/ObjectList';
import { useObjects } from '@hooks/useObjects.js';

const Objects = () => {
	const { md } = Grid.useBreakpoint();
	const { token } = theme.useToken();
	const [selectedUuid, setSelectedUuid] = useState(null);
	// The node whose edit form is open, if any. Selecting a node closes it.
	const [editingUuid, setEditingUuid] = useState(null);
	const [activeTab, setActiveTab] = useProjectTypeFilter();

	const handleSelect = uuid => {
		setSelectedUuid(uuid);
		setEditingUuid(null);
	};

	const handleEdit = object => {
		setSelectedUuid(object.uuid);
		setEditingUuid(object.uuid);
	};
	// Shares the tree's cached query; only the first root and the uuid set are read here.
	const { nodes, tree } = useObjects(activeTab);

	// The clicked node while it is in the tree on screen; otherwise the first root,
	// so the page opens on a selection and a project-type or org switch never
	// leaves the details pointing at an object that is no longer listed.
	const isListed = nodes.some(node => node.uuid === selectedUuid);
	const effectiveUuid = isListed ? selectedUuid : (tree[0]?.uuid ?? null);

	const separator = `1px solid ${token.colorBgElevated}`;

	// On wide screens the details stay in view while the (longer) tree scrolls
	// under them, so selecting a node far down the tree shows its details without
	// scrolling back up. Capped to the viewport with its own scroll for the
	// create form, which can be taller than the screen.
	const detailsPaneStyle = md
		? {
				position: 'sticky',
				top: space(12),
				maxHeight: `calc(100vh - ${space(6)})`,
				overflowY: 'auto',
				paddingInlineStart: space(4),
			}
		: undefined;

	return (
		<View
			title={'Objects'}
			subtitle={
				'Your organization taxonomy — Biodrone objects are locked, your own are editable'
			}>
			<ProjectTypeFilter activeTab={activeTab} onChange={setActiveTab} />
			{/* Stretch, not the default top alignment, or the details column would be as
			    short as its content and the sticky pane would have nothing to stick within. */}
			<Row gutter={[16]} align="stretch">
				<Col xs={{ flex: '100%' }} md={{ flex: 'auto' }}>
					<ObjectList
						mode={activeTab}
						selectedUuid={effectiveUuid}
						onSelect={handleSelect}
						onEdit={handleEdit}
					/>
				</Col>
				<Col
					xs={{ flex: '100%' }}
					md={{ flex: '400px' }}
					style={
						md
							? { borderInlineStart: separator }
							: { borderBlockStart: separator, paddingBlockStart: space(6) }
					}>
					<div style={detailsPaneStyle}>
						<ObjectDetails
							selectedUuid={effectiveUuid}
							mode={activeTab}
							editingUuid={editingUuid}
							onEditDone={() => setEditingUuid(null)}
						/>
					</div>
				</Col>
			</Row>
		</View>
	);
};

export default Objects;
